import { useEffect, useMemo, useState } from "react";
import MSINav from "./MSINav";
import {
  alertText,
  archiveAlert,
  markAlertSent,
  storageGet,
  storageSet,
} from "./msiAlertManager";

const panel = {
  background: "#171717",
  border: "1px solid #2A2A2A",
  borderRadius: 12,
  padding: 18,
};

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString([], {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function actionColor(action) {
  const a = String(action || "").toUpperCase();
  if (a.includes("EXIT") || a.includes("CUT")) return "#ef4444";
  if (a.includes("BUY") || a.includes("ENTER")) return "#22c55e";
  if (a.includes("TRAIL") || a.includes("PROTECT")) return "#f59e0b";
  return "#A0A0A0";
}

export default function MSIAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setAlerts(storageGet("msi_alerts", []));
  }, []);

  const visibleAlerts = useMemo(() => {
    return alerts
      .filter((a) => (showArchived ? true : !a.archived))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [alerts, showArchived]);

  const activeCount = alerts.filter((a) => !a.archived).length;
  const sentCount = alerts.filter((a) => a.sent && !a.archived).length;
  const archivedCount = alerts.filter((a) => a.archived).length;

  function showMessage(text) {
    setMessage(text);
    window.setTimeout(() => setMessage(""), 2500);
  }

  function refresh() {
    setAlerts(storageGet("msi_alerts", []));
    showMessage("Alerts refreshed.");
  }

  function copyOne(alert) {
    navigator.clipboard.writeText(alertText(alert));
    showMessage("Alert copied.");
  }

  function copyAllActive() {
    const active = alerts.filter((a) => !a.archived);
    const text = active
      .map(alertText)
      .join("\n\n----------------\n\n");
    navigator.clipboard.writeText(text);
    showMessage(`Copied ${active.length} active alert(s).`);
  }

  function markSent(alert) {
    setAlerts(markAlertSent(alert));
    showMessage("Alert marked sent.");
  }

  function archive(alert) {
    setAlerts(archiveAlert(alert));
    showMessage("Alert archived.");
  }

  function clearArchived() {
    const next = alerts.filter((a) => !a.archived);
    storageSet("msi_alerts", next);
    setAlerts(next);
    showMessage("Archived alerts cleared.");
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
      <h1 style={{ fontSize: 40, marginBottom: 4 }}>MSI Alerts</h1>
      <div style={{ color: "#A0A0A0", marginBottom: 16 }}>
        Syndication queue for buy, hold, protect, and exit alerts.
      </div>

      <MSINav />

      {message && (
        <div
          style={{
            ...panel,
            borderColor: "#2563eb",
            marginBottom: 14,
            color: "#D4D4D4",
            fontWeight: 800,
          }}
        >
          ✓ {message}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(160px, 1fr))", gap: 14, marginBottom: 18 }}>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Active Alerts</div><div style={{ fontSize: 32, fontWeight: 900 }}>{activeCount}</div></div>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Sent</div><div style={{ fontSize: 32, fontWeight: 900, color: "#22c55e" }}>{sentCount}</div></div>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Archived</div><div style={{ fontSize: 32, fontWeight: 900, color: "#A0A0A0" }}>{archivedCount}</div></div>
      </div>

      <div style={{ ...panel, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ color: "#A0A0A0", fontWeight: 800 }}>Showing {visibleAlerts.length} alert(s)</div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={refresh} style={btn()}>Refresh</button>
          <button onClick={copyAllActive} disabled={!activeCount} style={greenBtn(!!activeCount)}>Copy Active</button>
          <button onClick={() => setShowArchived((v) => !v)} style={btn()}>{showArchived ? "Hide Archived" : "Show Archived"}</button>
          <button onClick={clearArchived} disabled={!archivedCount} style={dangerBtn(!!archivedCount)}>Clear Archived</button>
        </div>
      </div>

      <div style={{ ...panel }}>
        {visibleAlerts.length === 0 && (
          <div style={{ padding: 24, color: "#A0A0A0" }}>
            No alerts yet. Alerts will be created from Watchlist and Positions actions.
          </div>
        )}

        {visibleAlerts.map((alert) => (
          <div
            key={alert.id}
            style={{
              borderBottom: "1px solid #242424",
              padding: "14px 0",
              display: "grid",
              gridTemplateColumns: "160px 1fr 140px 280px",
              gap: 14,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 900 }}>{alert.ticker} {alert.contract}</div>
              <div style={{ color: "#A0A0A0", fontSize: 13 }}>{fmtDate(alert.created_at)}</div>
            </div>

            <div>
              <div style={{ color: actionColor(alert.action), fontWeight: 900 }}>{alert.action}</div>
              <div style={{ color: "#A0A0A0", marginTop: 4 }}>{alert.target || "-"}</div>
              {alert.thesis && <div style={{ color: "#D4D4D4", fontSize: 13 }}>{alert.thesis}</div>}
            </div>

            <div>
              <div style={{ color: alert.sent ? "#22c55e" : "#A0A0A0", fontWeight: 900 }}>{alert.sent ? "SENT" : "UNSENT"}</div>
              <div style={{ color: alert.archived ? "#A0A0A0" : "#22c55e", fontSize: 13 }}>{alert.archived ? "ARCHIVED" : "ACTIVE"}</div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => copyOne(alert)} style={greenBtn(true)}>Copy</button>
              <button onClick={() => markSent(alert)} disabled={alert.sent} style={btn(!alert.sent)}>Mark Sent</button>
              <button onClick={() => archive(alert)} disabled={alert.archived} style={dangerBtn(!alert.archived)}>Archive</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function btn(enabled = true) {
  return {
    background: enabled ? "#262626" : "#171717",
    color: "white",
    border: "1px solid #3A3A3A",
    borderRadius: 8,
    padding: "9px 12px",
    fontWeight: 800,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
  };
}

function greenBtn(enabled) {
  return {
    background: enabled ? "#16a34a" : "#262626",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "9px 12px",
    fontWeight: 900,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
  };
}

function dangerBtn(enabled) {
  return {
    background: enabled ? "#ef4444" : "#262626",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "9px 12px",
    fontWeight: 900,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
  };
}
